import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

/**
 * Theme (light/dark) for the student study surfaces.
 *
 * How it works:
 *  - Resolved theme is applied as `data-theme="light|dark"` on <html>.
 *  - CSS reads it via `:root[data-theme="dark"] { --token: ... }` overrides
 *    defined in src/index.css. Components only ever reference var(--token),
 *    so a single attribute flip re-themes everything that has been tokenized.
 *  - Choice persists in localStorage under `lomi_theme`.
 *  - Default ('system') follows the device's prefers-color-scheme and keeps
 *    tracking it live until the student makes an explicit choice.
 */

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

interface ThemeContextValue {
  /** What the student picked ('system' means follow the device). */
  preference: ThemePreference;
  /** The concrete theme currently applied ('light' | 'dark'). */
  theme: ResolvedTheme;
  setPreference: (p: ThemePreference) => void;
  /** Flip between light and dark (sets an explicit preference). */
  toggle: () => void;
}

const STORAGE_KEY = 'lomi_theme';

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function readStoredPreference(): ThemePreference {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    /* localStorage may be unavailable (private mode / WebView) */
  }
  // Default to explicit light (opt-in). We deliberately do NOT default to
  // 'system' yet: while dark mode is rolling out surface-by-surface, an
  // auto-dark device would render not-yet-converted pages (Profile, exams
  // history, etc.) in broken half-light. Switch this to 'system' once the
  // whole logged-in experience is dark-ready.
  return 'light';
}

function resolve(pref: ThemePreference): ResolvedTheme {
  if (pref === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return pref;
}

function applyTheme(theme: ResolvedTheme) {
  const root = document.documentElement;
  root.setAttribute('data-theme', theme);
  // Hint native form controls / scrollbars to match.
  root.style.colorScheme = theme;
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [preference, setPreferenceState] = useState<ThemePreference>(readStoredPreference);
  const [theme, setTheme] = useState<ResolvedTheme>(() => resolve(readStoredPreference()));

  // Apply the resolved theme to <html> whenever it changes.
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // While on 'system', track live OS changes.
  useEffect(() => {
    if (preference !== 'system' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setTheme(mq.matches ? 'dark' : 'light');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [preference]);

  const setPreference = useCallback((p: ThemePreference) => {
    setPreferenceState(p);
    setTheme(resolve(p));
    try {
      localStorage.setItem(STORAGE_KEY, p);
    } catch {
      /* ignore */
    }
  }, []);

  const toggle = useCallback(() => {
    setPreference(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setPreference]);

  return (
    <ThemeContext.Provider value={{ preference, theme, setPreference, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
};

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
